import { Button, Flex, Tabs } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { useZodForm } from '../../hooks/useZodForm.ts';
import { setSettings } from '../../redux/features/actions/setSettings.ts';
import { selectSettingsFormValues } from '../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { Depreciation } from './Depreciation.tsx';
import { RoundingAndThresholds } from './RoundingAndThresholds.tsx';
import { SettingsFormOutput, schSettingsForm } from './schSettingsForm.ts';
import { CostMultiplier } from './CostMultiplier.tsx';

const tabs = {
  rounding: 'Rounding & Thresholds',
  depreciation: 'Depreciation',
  costMultiplier: 'Cost Multiplier',
};

type Tab = keyof typeof tabs;

type Props = {
  modalOnClose: () => void;
};

export const SettingsModalContent = ({ modalOnClose }: Props) => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<Tab>('rounding');

  const defaultValues = useAppSelector(selectSettingsFormValues);

  const settingsForm = useZodForm({
    defaultValues,
    schema: schSettingsForm,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  function onSubmit(data: SettingsFormOutput): void {
    dispatch(setSettings(data));
  }

  function onError(errors: Record<string, unknown>): void {
    console.error('error submitting form', errors);
  }

  function onSubmitAsDefault(data: SettingsFormOutput): void {
    dispatch(
      setSettings({
        ...data,
        saveAsDefault: true,
      }),
    );
  }
  const save = settingsForm.handleSubmit(onSubmit, onError);
  const saveAsDefault = settingsForm.handleSubmit(onSubmitAsDefault, onError);

  function saveAndClose(): void {
    save();
    modalOnClose();
  }

  function saveAndSetActiveTab(tab: string): void {
    save();
    setActiveTab(tab as Tab);
  }

  function saveAsDefaultAndClose(): void {
    saveAsDefault();
    modalOnClose();
  }

  function getTabContent() {
    switch (activeTab) {
      case 'depreciation':
        return <Depreciation />;
      case 'rounding':
        return <RoundingAndThresholds />;
      case 'costMultiplier':
        return <CostMultiplier />;
      default:
        return null;
    }
  }

  return (
    <FormProvider {...settingsForm}>
      <Tabs
        variant="secondary"
        tabs={Object.entries(tabs).map(([value, label]) => ({ value, label }))}
        activeTab={activeTab}
        setActiveTab={saveAndSetActiveTab}
      />
      <Flex padding="1rem">{getTabContent()}</Flex>
      <Flex justifyContent="flex-end" gap=".75rem" marginTop="1rem">
        {activeTab !== 'costMultiplier' && (
          <Button
            variant="inline"
            text="Save as Default for New Orders"
            onClick={saveAsDefaultAndClose}
          />
        )}

        <Button
          variant="primary"
          text="Close"
          onClick={saveAndClose}
          __dangerDoNotUseSx={{ paddingInline: '20px' }}
        />
      </Flex>
    </FormProvider>
  );
};
